/**
 * EPIC283-F Since control presenter.
 * Consumes K1 since_control honestly — descriptive change language only,
 * no causal claims, no invented history or control titles.
 */

import { sinceControlClaimComparisonAllowed } from '../continuousSafety/measurementContract';
import {
  supportingActivityTotal,
  type SiteConditionMeasurementWindow,
} from '../continuousSafety/siteConditionState';
import {
  comparabilityLabel,
  displayStateContent,
  formatDisplayMessage,
  proofSuccessLanguage,
  type DisplayStateKey,
} from './contentContract';

const KNOWN_STATES: ReadonlySet<string> = new Set([
  'current',
  'current_with_limitations',
  'delayed',
  'stale',
  'never_measured',
  'scan_only_collecting',
  'unavailable',
  'readiness_unavailable',
  'empty',
  'blocked',
  'unauthorized',
  'failure',
]);

export type SinceControlComparability =
  | 'valid'
  | 'limited'
  | 'cannot_compare'
  | 'inconclusive';

export type SinceControlPresentation = {
  stateKey: DisplayStateKey;
  stateLabel: string;
  message: string;
  limitation: string;
  primaryCta: string;
  /** Control-anchor framing label (not a Current-shift claim). */
  shiftLabel: string;
  asOfLabel: string;
  /** K1 window_anchor_id when present; null when no control anchor. */
  controlAnchorId: string | null;
  controlAnchorLine: string;
  /** Descriptive result / why unavailable — never causal. */
  resultLine: string;
  comparability: SinceControlComparability;
  comparabilitySummary: string;
  basisSummary: string | null;
  coverageSummary: string | null;
  finalitySummary: string | null;
  /** True when proof/change claims must not be shown as final. */
  comparisonBlocked: boolean;
  methodologySummary: string;
};

function mapDisplayStateKey(raw: string | null | undefined): DisplayStateKey {
  if (!raw) return 'unavailable';
  const normalized = raw.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'current_with_limitation') return 'current_with_limitations';
  if (KNOWN_STATES.has(normalized)) return normalized as DisplayStateKey;
  return 'unavailable';
}

function resolveAsOf(window: SiteConditionMeasurementWindow | null): string {
  if (!window) return '—';
  return window.displayAsOf || window.observedThrough || '—';
}

function resolveFinality(window: SiteConditionMeasurementWindow | null): string | null {
  if (!window || window.buckets.length === 0) return null;
  const allFinalized = window.buckets.every(
    (bucket) => bucket.bucketFinality === 'finalized',
  );
  return allFinalized ? 'finalized' : 'provisional';
}

function isPartialWindow(window: SiteConditionMeasurementWindow | null): boolean {
  if (!window) return false;
  if (window.availability === 'limited') return true;
  if (window.buckets.some((bucket) => bucket.isPartial)) return true;
  return mapDisplayStateKey(window.displayState) === 'current_with_limitations';
}

function hasAnchor(window: SiteConditionMeasurementWindow | null): boolean {
  return Boolean(window?.windowAnchorId && String(window.windowAnchorId).trim());
}

function reasonMentionsInconclusive(window: SiteConditionMeasurementWindow | null): boolean {
  if (!window) return false;
  return window.reasonCodes.some((code) =>
    String(code).toLowerCase().includes('inconclusive'),
  );
}

function claimGateInput(window: SiteConditionMeasurementWindow) {
  const coverage = window.summary.measurementCoverage || null;
  return {
    comparisonStatus: window.comparisonStatus,
    coverageGatePassed: coverage?.coverageGatePassed,
    reviewGatePassed: coverage?.reviewGatePassed,
    reviewedObservationCount: coverage?.reviewedObservationCount,
    ratePerHour: coverage?.ratePerHour,
  };
}

function resolveComparability(
  window: SiteConditionMeasurementWindow | null,
): SinceControlComparability {
  if (!window || !hasAnchor(window)) return 'cannot_compare';

  const status = (window.comparisonStatus || '').toLowerCase();
  if (status === 'inconclusive' || reasonMentionsInconclusive(window)) {
    return 'inconclusive';
  }
  if (
    status === 'comparison_unavailable'
    || status === 'not_comparable'
    || status === 'cannot_compare'
    || window.availability === 'unavailable'
    || window.displayState === 'unavailable'
  ) {
    return 'cannot_compare';
  }
  // Claim packaging: fingerprint-comparable alone is not enough. Require
  // reviewed-exhaustive rate issuance facts; never promote operational activity.
  if (!sinceControlClaimComparisonAllowed(claimGateInput(window))) {
    return 'cannot_compare';
  }
  if (
    status === 'limited'
    || window.availability === 'limited'
    || isPartialWindow(window)
  ) {
    // Limited comparison is still not a final proof claim.
    if (status === 'comparable') return 'limited';
    if (!status || status === 'limited') return 'limited';
  }
  if (status === 'comparable') return 'valid';
  // Unknown status with an anchor: fail closed — do not invent validity.
  return 'cannot_compare';
}

function resolveStateKey(
  window: SiteConditionMeasurementWindow | null,
  comparability: SinceControlComparability,
): DisplayStateKey {
  if (!window || !hasAnchor(window)) return 'blocked';
  if (
    window.availability === 'unavailable'
    || window.displayState === 'unavailable'
    || comparability === 'cannot_compare'
  ) {
    if (window.buckets.length === 0 && window.availability !== 'unavailable') {
      return 'empty';
    }
    // Prefer blocked when anchor/comparison prevents a since-control claim.
    return comparability === 'cannot_compare' && window.availability !== 'unavailable'
      ? 'blocked'
      : 'unavailable';
  }
  if (comparability === 'inconclusive') {
    // Workflow complete — not a Current Now claim; keep measured display_state when present.
    const mapped = mapDisplayStateKey(window.displayState);
    return mapped === 'unavailable' ? 'empty' : mapped;
  }
  if (comparability === 'limited') {
    const mapped = mapDisplayStateKey(window.displayState);
    if (mapped === 'current') return 'current_with_limitations';
    return mapped;
  }
  return mapDisplayStateKey(window.displayState);
}

function formatReason(window: SiteConditionMeasurementWindow | null): string {
  const code = window?.reasonCodes?.[0];
  if (!code) return '';
  return ` Reason: ${String(code).replace(/_/g, ' ')}.`;
}

function buildComparabilitySummary(
  window: SiteConditionMeasurementWindow | null,
  comparability: SinceControlComparability,
): string {
  if (comparability === 'valid') {
    return `${comparabilityLabel('valid')}. ${proofSuccessLanguage()}.`;
  }
  if (comparability === 'limited') {
    return `${comparabilityLabel('limited')} — do not treat change as final.${formatReason(window)}`;
  }
  if (comparability === 'inconclusive') {
    return comparabilityLabel('inconclusive');
  }
  if (!window || !hasAnchor(window)) {
    return `${comparabilityLabel('cannot_compare')} — no implemented control anchor is available for this site condition.`;
  }
  const status = (window.comparisonStatus || '').toLowerCase();
  if (
    status === 'comparable'
    && !sinceControlClaimComparisonAllowed(claimGateInput(window))
  ) {
    return (
      `${comparabilityLabel('cannot_compare')} — reviewed exhaustive rate is not `
      + 'issuable for this since-control window (coverage/review gates or rate missing). '
      + 'Operational activity is not compared as a claim. No descriptive change claim.'
    );
  }
  return `${comparabilityLabel('cannot_compare')}.${formatReason(window)} No descriptive change claim.`;
}

function buildResultLine(params: {
  window: SiteConditionMeasurementWindow | null;
  comparability: SinceControlComparability;
}): string {
  const { window, comparability } = params;
  if (!window || !hasAnchor(window)) {
    return 'Since-control comparison is unavailable — no control anchor is selected or recorded. History is not fabricated.';
  }
  if (comparability === 'cannot_compare') {
    const status = (window.comparisonStatus || '').toLowerCase();
    if (
      status === 'comparable'
      && !sinceControlClaimComparisonAllowed(claimGateInput(window))
    ) {
      return (
        'Cannot compare since control — exhaustive review/coverage gates did not '
        + 'issue a reviewed rate. Operational activity is not a claim-bearing comparison.'
      );
    }
    return `Cannot compare since control${formatReason(window)} Descriptive change is not claimed.`;
  }
  if (comparability === 'inconclusive') {
    return `${comparabilityLabel('inconclusive')}. Close or continue the proof cycle without treating this as a favorable change.`;
  }
  if (comparability === 'limited') {
    // Facts surface: bucket activity only — never claim measurementValue.
    const value = supportingActivityTotal(window);
    const valuePart =
      value != null && Number.isFinite(value)
        ? ` Qualifying activity since control: ${value}.`
        : '';
    return `${comparabilityLabel('limited')}.${valuePart} Do not treat provisional or partial follow-up as final proof.`;
  }

  // Valid — reviewed exhaustive facts only; never invent decreased/increased labels.
  const coverage = window.summary.measurementCoverage;
  const reviewed = coverage?.reviewedObservationCount;
  const rate = coverage?.ratePerHour;
  if (
    reviewed != null
    && Number.isFinite(reviewed)
    && rate != null
    && Number.isFinite(rate)
  ) {
    return (
      `${proofSuccessLanguage()}. Confirmed reviewed observations since control: `
      + `${reviewed} (${rate}/usable hour). Direction labels are not invented from site-state.`
    );
  }
  return `${proofSuccessLanguage()}. Follow-up reviewed measurement is present under a comparable basis.`;
}

function basisSummary(window: SiteConditionMeasurementWindow | null): string | null {
  if (!window?.basis?.type) {
    return 'Measurement basis is not available for this since-control window.';
  }
  const units = window.basis.units ? ` · ${window.basis.units}` : '';
  const version = window.basis.version ? ` · ${window.basis.version}` : '';
  return `Basis: ${window.basis.type}${units}${version}`;
}

function coverageSummary(
  window: SiteConditionMeasurementWindow | null,
  partial: boolean,
): string | null {
  if (!window) return 'Coverage unavailable for since-control follow-up.';
  const bucketCount = window.summary.bucketCount || window.buckets.length;
  if (partial) {
    return `Partial follow-up — ${bucketCount} measured bucket${
      bucketCount === 1 ? '' : 's'
    }; missing periods are not invented.`;
  }
  if (bucketCount === 0) {
    return 'No measured follow-up buckets since the control anchor yet.';
  }
  return `${bucketCount} measured bucket${bucketCount === 1 ? '' : 's'} since the control anchor.`;
}

function finalitySummary(finality: string | null): string | null {
  if (!finality) return null;
  if (finality === 'finalized') {
    return 'Finality: finalized for the buckets in this since-control window.';
  }
  return 'Finality: provisional — not a final proof claim.';
}

function controlAnchorLine(window: SiteConditionMeasurementWindow | null): string {
  if (!window || !hasAnchor(window)) {
    return 'Control anchor: none — since-control comparison is blocked until an implemented control revision exists.';
  }
  return `Control anchor: ${String(window.windowAnchorId).trim()}`;
}

/**
 * Present Since control from a K1 since_control window.
 * Does not invent trends, causal effectiveness, or missing control history.
 */
export function presentSinceControl(params: {
  window?: SiteConditionMeasurementWindow | null;
  dataNote?: string | null;
}): SinceControlPresentation {
  const window = params.window || null;
  const partial = isPartialWindow(window);
  const comparability = resolveComparability(window);
  const comparisonBlocked = comparability !== 'valid';
  const stateKey = resolveStateKey(window, comparability);
  const block = displayStateContent(stateKey);
  const asOfLabel = resolveAsOf(window);
  const finality = resolveFinality(window);
  const anchorPresent = hasAnchor(window);

  let message: string;
  if (!anchorPresent) {
    message = 'Since-control comparison is blocked until an implemented control is available';
  } else if (
    stateKey === 'unavailable'
    || stateKey === 'empty'
    || stateKey === 'never_measured'
    || stateKey === 'blocked'
  ) {
    message = block.message;
  } else if (stateKey === 'current' || stateKey === 'current_with_limitations') {
    message = `Since control through ${asOfLabel}`;
  } else {
    message = formatDisplayMessage(stateKey, asOfLabel);
  }

  let limitation = block.limitation;
  if (comparability === 'inconclusive') {
    limitation = [
      comparabilityLabel('inconclusive'),
      'Do not treat inconclusive as a favorable change.',
      limitation,
    ]
      .filter(Boolean)
      .join(' ');
  } else if (comparability === 'cannot_compare') {
    limitation = [
      'Valid since-control comparison is unavailable; no descriptive change is claimed.',
      limitation,
    ]
      .filter(Boolean)
      .join(' ');
  } else if (comparability === 'limited' || partial) {
    limitation = [
      'Partial or limited follow-up — do not invent missing pre/post history.',
      limitation,
    ]
      .filter(Boolean)
      .join(' ');
  }
  if (params.dataNote) {
    limitation = `${limitation} ${params.dataNote}`.trim();
  }

  const shiftLabel = anchorPresent
    ? 'Since control'
    : 'Since control · no anchor';

  return {
    stateKey,
    stateLabel: block.label,
    message,
    limitation,
    primaryCta: block.primaryCta,
    shiftLabel,
    asOfLabel,
    controlAnchorId: anchorPresent ? String(window!.windowAnchorId).trim() : null,
    controlAnchorLine: controlAnchorLine(window),
    resultLine: buildResultLine({ window, comparability }),
    comparability,
    comparabilitySummary: buildComparabilitySummary(window, comparability),
    basisSummary: basisSummary(window),
    coverageSummary: coverageSummary(window, partial),
    finalitySummary: finalitySummary(finality),
    comparisonBlocked,
    methodologySummary:
      'Since-control measurement describes observed activity after an implemented control under a declared basis — not whether site conditions are good or bad, and not causal control effectiveness.',
  };
}

/** Extract K1 since_control window from the first condition (passthrough; may be empty). */
export function sinceControlWindowFromSiteConditionResponse(data: {
  conditions?: Array<{
    operationalMeasurements?: {
      sinceControl?: SiteConditionMeasurementWindow;
    };
  }>;
} | null | undefined): SiteConditionMeasurementWindow | null {
  return data?.conditions?.[0]?.operationalMeasurements?.sinceControl || null;
}
